<template>
    <div class="container">
        <h1 class="my-3">{{ $t('nav.devices') }}</h1>

        <div class="input-group mb-3 rounded-pill">
            <input type="text" class="form-control" :placeholder="$t('browse.searchObjects')" v-model="searchTerm"
                @keyup.enter.prevent="onSearchSubmit" />
            <span class="input-group-text bg-body" type="button" @click="onSearchSubmit">
                <i class="fa-solid fa-magnifying-glass"></i>
            </span>
        </div>

        <div v-if="objects.length > 0">
            <ul class="list-group mb-3">
                <li v-for="object in objects" :key="object.id" class="list-group-item p-3">
                    <!-- keep query so Back returns to same page/search -->
                    <router-link :to="`/objects/${object.id}`"
                        class="text-decoration-none">
                        <div class="row align-items-center">
                            <div class="col">
                                <div class="fw-bolder small">{{ object.name }}</div>
                                <div class="small">{{ object.project_name }}</div>
                            </div>
                            <div class="col-auto text-end">
                                <button class="btn-icon"><i class="fa-solid fa-arrow-right"></i></button>
                            </div>
                        </div>
                    </router-link>
                </li>
            </ul>

            <ListPagination :currentPage="currentPage" :totalTasks="totalItems" :limit="limit"
                @page-changed="onPageChanged" />
        </div>
        <div v-else></div>
    </div>
</template>

<script>
import axios from '@/services/axios';
import ListPagination from '@/components/ListPagination.vue';

export default {
    components: { ListPagination },

    data() {
        return {
            objects: [],
            currentPage: 1,   // mirrors ?page=
            totalItems: 0,
            limit: 20,
            searchTerm: '',   // mirrors ?search=
            lastRouteKey: '', // avoid duplicate fetches
        };
    },

    mounted() {
        // no manual fetch; watcher below runs immediately
    },

    watch: {
        '$route.query': {
            immediate: true,
            deep: true,
            handler(newQ) {
                // normalize page
                const pRaw = newQ.page ?? '1';
                const p = parseInt(pRaw, 10);
                if (!Number.isFinite(p) || p < 1) {
                    this.$router.replace({ query: { ...newQ, page: '1' } });
                    return;
                }

                // normalize search
                const s = (newQ.search ?? '').toString();

                // keep input synced with URL
                if (this.searchTerm !== s) this.searchTerm = s;

                const key = `${p}|${s}`;
                if (key === this.lastRouteKey) return;
                this.lastRouteKey = key;

                this.currentPage = p;
                this.fetchObjects(p, s);
            }
        }
    },

    methods: {
        onSearchSubmit() {
            const term = (this.searchTerm || '').trim();
            const q = { ...this.$route.query, page: '1' };
            if (term) q.search = term; else delete q.search;
            this.$router.push({ query: q }); // watcher will fetch
        },

        onPageChanged(newPage) {
            const q = { ...this.$route.query, page: String(newPage) };
            if (q.page !== this.$route.query.page) this.$router.push({ query: q });
            // watcher will fetch
        },

        async fetchObjects(page = 1, searchTerm = '') {
            try {
                const params = new URLSearchParams();
                const projectId = this.$route.params.projectId;
                if (projectId) params.append('project_id', projectId);

                const url =
                    `/objects?${params.toString()}${params.toString() ? '&' : ''}` +
                    `not_active=0&page=${page}&limit=${this.limit}&search=${encodeURIComponent(searchTerm)}`;

                const { data } = await axios.get(url);
                this.objects = data.data;
                this.totalItems = data.total;
                this.currentPage = page;
            } catch (error) {
                console.error('Error fetching objects:', error);
            }
        }
    }
};
</script>
